import { isMembershipLine } from '@notifi/shared-types/membership-line';
import { resolveEstimateLineAmounts } from '@/lib/sales/line-item-pricing';

/**
 * Deposit due for the customer's CURRENT optional-item selections.
 *
 * WHY: on the public quote every summary row is derived from the selected line
 * items, except the deposit, which used to read the stored `depositAmount`.
 * That made a PERCENT deposit look like a flat fee — toggling an optional item
 * moved the total while the deposit sat still, then it jumped at approval once
 * the server repriced. This recomputes it so the preview tracks the total.
 *
 * NOT-604 — the base is the deposit-ELIGIBLE total, not the grand total. A
 * membership is a recurring subscription, not deposit-eligible work, so its
 * price (and any tax on it) never inflates the deposit: $1,000 job + $200
 * membership + 10% deposit is $100 due, not $120. The server already computes
 * it this way (`deposit-base.util.ts` `depositEligibleTotal`, persisted on the
 * estimate and re-derived at customerApprove); this mirrors it so the preview,
 * the signed PDF and the charge all print the same number. Derived from the
 * SELECTED lines rather than the stored `depositEligibleTotal` column so it
 * stays correct while the customer toggles optional lines — the same reason
 * every other summary row here is derived. Membership lines are classified by
 * the shared predicate (`@notifi/shared-types/membership-line`), the single
 * source of truth both apps use, and each line's contribution comes through
 * `resolveEstimateLineAmounts` — the same resolver that produced the page's
 * grand total, so the subtraction can never disagree with the sum.
 *
 * Mirrors the server's `DepositEngineService.calculateDocumentAmount` +
 * `calculate` pair so the previewed figure equals what approval persists and
 * what Stripe is asked to charge:
 *  - PERCENT: percentage clamped to 0–100, applied to the eligible total.
 *    (The clamp lives in `calculate`, not `calculateDocumentAmount` — the outer
 *    function only floors the value at 0.)
 *  - FLAT: floored at 0 and capped at the eligible total, never more than the
 *    deposit-eligible work is worth (the server passes `depositEligibleTotal`
 *    as `calculateDocumentAmount`'s total, so the cap is eligible there too).
 *  - No type (legacy rows): the stored amount, clamped into 0–eligible.
 *
 * Pinned once the deposit is collected, matching the server's
 * `depositAlreadyCollected ? estimate.depositAmount` branch — money already
 * taken is never re-derived from a total the customer can still change.
 *
 * Extracted as a pure function so this rule is unit-testable independently of
 * the large public quote route component, the same reason `depositCta.ts` and
 * the other helpers in this directory were.
 */

/**
 * The fields the deposit base reads off a selected line: the two membership
 * tells, plus the money fields `resolveEstimateLineAmounts` resolves. Both the
 * public quote page's line shape and the portal's satisfy it structurally.
 * NOTE: the public projection ships `type` but strips `taxCategory`, so on the
 * public page the taxCategory tell can never fire — a membership stored there
 * is typed MEMBERSHIP.
 */
export interface DepositBaseLine {
  type?: string | null;
  taxCategory?: string | null;
  lineSubtotal?: unknown;
  lineTax?: unknown;
  lineTotal?: unknown;
}

export function selectedDepositAmount(input: {
  depositAmount: number | null | undefined;
  depositType: string | null | undefined;
  depositValue: number | null | undefined;
  depositCollected: boolean | null | undefined;
  selectedGrandTotal: number;
  /** The lines currently in the sale — required lines plus ticked offers. */
  selectedLines: readonly DepositBaseLine[];
}): number {
  const stored = Number(input.depositAmount) || 0;
  if (input.depositCollected) return stored;

  const cap = selectedDepositEligibleTotal(input.selectedGrandTotal, input.selectedLines);
  const value = Number(input.depositValue) || 0;

  if (input.depositType === 'PERCENT') {
    return roundCurrency(cap * (Math.min(Math.max(value, 0), 100) / 100));
  }
  if (input.depositType === 'FLAT') {
    return Math.min(Math.max(value, 0), cap);
  }
  return Math.min(Math.max(stored, 0), cap);
}

/**
 * The deposit base for the current selections: the selected grand total minus
 * every selected MEMBERSHIP line's post-tax total. Mirrors the server's
 * `depositEligibleTotal(grandTotal, lineItems)` (which excludes on the same
 * shared predicate and only counts selected lines — the callers here pass only
 * selected lines, so the selection rule is already applied). Never below 0.
 */
export function selectedDepositEligibleTotal(
  selectedGrandTotal: number,
  selectedLines: readonly DepositBaseLine[],
): number {
  const grand = Math.max(0, Number(selectedGrandTotal) || 0);
  const membershipTotal = selectedLines.reduce(
    (sum, line) =>
      isMembershipLine(line) ? sum + resolveEstimateLineAmounts(line).lineTotal : sum,
    0,
  );
  return Math.max(0, roundCurrency(grand - membershipTotal));
}

function roundCurrency(amount: number): number {
  return Math.round(amount * 100) / 100;
}
